import React, { useState } from 'react';
import { 
  MagnifyingGlassIcon, 
  CalendarIcon,
  IdentificationIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  ExclamationCircleIcon,
  ArrowRightIcon
} from '@heroicons/react/24/outline';
import Header from '../components/common/Header';
import Footer from '../components/common/Footer';
import { checkApplicationStatus } from '../services/firebaseService';
import { Application } from '../types';
import { formatDate } from '../utils/helpers';

const CheckStatusPage: React.FC = () => {
  const [appId, setAppId] = useState('');
  const [dob, setDob] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<Application | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleCheckStatus = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!appId || !dob) return;

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const application = await checkApplicationStatus(appId, dob);
      if (application) {
        setResult(application);
      } else {
        setError('No registration found with the provided details. Please check your Registration Number and Date of Birth.');
      }
    } catch (err) {
      console.error(err);
      setError('An error occurred while checking your status. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const getStatusDisplay = (status: Application['status']) => {
    switch (status) {
      case 'verified':
        return {
          icon: <CheckCircleIcon className="w-12 h-12 text-green-600" />,
          title: 'Verified & Approved',
          color: 'bg-green-50 text-green-800 border-green-200',
          description: 'Congratulations! Your application has been verified and approved. You will receive further instructions via email.'
        };
      case 'rejected':
        return {
          icon: <XCircleIcon className="w-12 h-12 text-red-600" />,
          title: 'Application Rejected',
          color: 'bg-red-50 text-red-800 border-red-200',
          description: 'We regret to inform you that your application was not successful at this time.'
        };
      case 'under_review':
        return {
          icon: <ClockIcon className="w-12 h-12 text-yellow-600" />,
          title: 'Under Review',
          color: 'bg-yellow-50 text-yellow-800 border-yellow-200',
          description: 'Your application is currently being reviewed by our selection committee. This typically takes 7-10 working days.'
        };
      case 'clarification_requested':
        return {
          icon: <ExclamationCircleIcon className="w-12 h-12 text-orange-600" />,
          title: 'Clarification Needed',
          color: 'bg-orange-50 text-orange-800 border-orange-200',
          description: 'We need some additional information from you. Please check your email for details.'
        };
      case 'on_hold':
        return {
          icon: <ClockIcon className="w-12 h-12 text-blue-600" />,
          title: 'On Hold',
          color: 'bg-blue-50 text-blue-800 border-blue-200',
          description: 'Your application has been placed on hold. We will get back to you soon.'
        };
      default:
        return {
          icon: <ClockIcon className="w-12 h-12 text-primary-600" />,
          title: 'Submitted',
          color: 'bg-primary-50 text-primary-800 border-primary-200',
          description: 'Your application has been received and is waiting for initial screening.'
        };
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header transparent={false} />
      
      <main className="pt-40 pb-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            {/* Page Header */}
            <div className="text-center mb-12">
              <h1 className="text-4xl font-bold text-gray-900 mb-4">
                Check Registration Status
              </h1>
              <p className="text-lg text-gray-600">
                Enter your details below to track your registration progress in real-time.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
              {/* Form Section */}
              <div className="md:col-span-5">
                <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
                  <form onSubmit={handleCheckStatus} className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                        <IdentificationIcon className="w-4 h-4 mr-2 text-primary-600" />
                        Registration Number
                      </label>
                      <input
                        type="text"
                        value={appId}
                        onChange={(e) => setAppId(e.target.value)}
                        className="form-input"
                        placeholder="e.g. app_123456789"
                        required
                      />
                      <p className="mt-1 text-xs text-gray-500">
                        Check your confirmation email for your registration ID.
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                        <CalendarIcon className="w-4 h-4 mr-2 text-primary-600" />
                        Date of Birth
                      </label>
                      <input
                        type="date"
                        value={dob}
                        onChange={(e) => setDob(e.target.value)}
                        className="form-input"
                        required
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={loading}
                      className="btn-primary w-full flex items-center justify-center space-x-2"
                    >
                      {loading ? (
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      ) : (
                        <>
                          <MagnifyingGlassIcon className="w-5 h-5" />
                          <span>Track Registration</span>
                        </>
                      )}
                    </button>
                  </form>
                </div>
              </div>

              {/* Result Section */}
              <div className="md:col-span-7">
                {loading ? (
                  <div className="bg-white rounded-2xl shadow-xl p-12 border border-gray-100 flex flex-col items-center justify-center h-full">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mb-4"></div>
                    <p className="text-gray-500 font-medium">Fetching registration details...</p>
                  </div>
                ) : result ? (
                  <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100 h-full animate-fade-in">
                    {/* Status Banner */}
                    <div className={`p-6 border-b flex items-center space-x-4 ${getStatusDisplay(result.status).color}`}>
                      {getStatusDisplay(result.status).icon}
                      <div>
                        <h3 className="text-2xl font-bold">{getStatusDisplay(result.status).title}</h3>
                        <p className="text-sm opacity-90">Registration: {result.registrationNumber || result.id}</p>
                      </div>
                    </div>

                    <div className="p-8">
                      <div className="space-y-6">
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="text-sm text-gray-500 uppercase tracking-wider font-semibold mb-1">Applicant Name</p>
                            <p className="text-xl font-bold text-gray-900">{result.firstName} {result.lastName}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm text-gray-500 uppercase tracking-wider font-semibold mb-1">Submission Date</p>
                            <p className="text-lg font-medium text-gray-900">{formatDate(result.submittedAt)}</p>
                          </div>
                        </div>

                        <div className="bg-gray-50 rounded-xl p-6">
                          <h4 className="font-bold text-gray-900 mb-3 flex items-center">
                            <ClockIcon className="w-5 h-5 mr-2 text-primary-600" />
                            Current Status Description
                          </h4>
                          <p className="text-gray-700 leading-relaxed">
                            {getStatusDisplay(result.status).description}
                          </p>
                        </div>

                        {result.reviewNotes && (result.status === 'clarification_requested' || result.status === 'on_hold') && (
                          <div className="bg-orange-50 border border-orange-100 rounded-xl p-6">
                            <h4 className="font-bold text-orange-900 mb-3 flex items-center">
                              <ExclamationCircleIcon className="w-5 h-5 mr-2" />
                              Official Remarks
                            </h4>
                            <p className="text-orange-800">
                              {result.reviewNotes}
                            </p>
                          </div>
                        )}

                        <div className="pt-6 border-t border-gray-100">
                          <p className="text-sm text-gray-500 mb-4">
                            If you have any questions regarding your registration status, please contact our support team.
                          </p>
                          <a 
                            href="mailto:kreedodisha@gmail.com" 
                            className="text-primary-600 font-bold hover:underline inline-flex items-center"
                          >
                            Contact Support
                            <ArrowRightIcon className="w-4 h-4 ml-2" />
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : error ? (
                  <div className="bg-white rounded-2xl shadow-xl p-12 border border-red-100 flex flex-col items-center justify-center h-full text-center">
                    <XCircleIcon className="w-16 h-16 text-red-500 mb-4" />
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Check Failed</h3>
                    <p className="text-gray-600 max-w-xs">{error}</p>
                  </div>
                ) : (
                  <div className="bg-white rounded-2xl shadow-xl p-12 border border-gray-100 flex flex-col items-center justify-center h-full text-center text-gray-400">
                    <MagnifyingGlassIcon className="w-16 h-16 mb-4 opacity-20" />
                    <h3 className="text-xl font-bold mb-2">Track Your Registration</h3>
                    <p className="max-w-xs">Enter your registration number and DOB to see the current status of your registration.</p>
                  </div>
                )}
              </div>
            </div>

            {/* FAQ Section */}
            <div className="mt-16 bg-white rounded-2xl shadow-lg p-8 border border-gray-100">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Frequently Asked Questions</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <h4 className="font-bold text-gray-900 mb-2">How long does the review take?</h4>
                  <p className="text-gray-600 text-sm">
                    Initial screening typically takes 7-10 working days. Depending on the volume of applications, it might take longer.
                  </p>
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 mb-2">I lost my Registration Number</h4>
                  <p className="text-gray-600 text-sm">
                    Please check the confirmation email you received after submission. If you still can't find it, contact kreedodisha@gmail.com.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default CheckStatusPage;
